/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1675814400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Feb 08 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "heap",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Heap sync types",
          "depth": 0,
          "id": "heap-sync-types"
        }, {
          "value": "Syncing objects",
          "depth": 0,
          "id": "syncing-objects"
        }, {
          "value": "Object sync modes",
          "depth": 1,
          "id": "object-sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "A note on syncing account properties",
          "depth": 1,
          "id": "a-note-on-syncing-account-properties"
        }, {
          "value": "Syncing events",
          "depth": 0,
          "id": "syncing-events"
        }, {
          "value": "Event sync modes",
          "depth": 1,
          "id": "event-sync-modes"
        }, {
          "value": "Event name",
          "depth": 1,
          "id": "event-name"
        }, {
          "value": "Timestamp",
          "depth": 1,
          "id": "timestamp"
        }, {
          "value": "Identity",
          "depth": 1,
          "id": "identity"
        }, {
          "value": "Field mapping (events and objects)",
          "depth": 0,
          "id": "field-mapping-events-and-objects"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      em: "em",
      img: "img",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      strong: "strong",
      blockquote: "blockquote",
      a: "a"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "Upon selecting Heap as your destination you will be prompted to\ninput your Heap Environment ID, which correspond to the specific project environment\nyou wish to sync your data to."
      }), "\n", _jsxs(_components.p, {
        children: ["You can locate the appropriate Environment ID within your Heap account by navigating to\n", _jsx(_components.em, {
          children: "Account > Manage > Projects"
        }), " and clicking on your desired project:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-heap-setup.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "heap-sync-types",
        children: "Heap sync types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing the following Heap sync types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Events"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Objects"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "User Properties"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "Account Properties"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-objects",
        children: "Syncing objects"
      }), "\n", _jsx(_components.h3, {
        id: "object-sync-modes",
        children: "Object sync modes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new objects to Heap ", _jsx(_components.em, {
            children: "and"
          }), " updates fields that change in your warehouse."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to your Userflow workspace by the ", _jsx(_components.code, {
          children: "identity"
        }), " (typically a unique user property)\nor ", _jsx(_components.code, {
          children: "account_id"
        }), ", depending on the object you are syncing."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-heap-object-record-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "a-note-on-syncing-account-properties",
        children: "A note on syncing account properties"
      }), "\n", _jsxs(_components.p, {
        children: ["To sync account properties you must first have users synced into your Heap workspace.\nThe reason for this is that to sync account properties you must first configure Account IDs.\nThis may be done by navigating to ", _jsx(_components.em, {
          children: "Account > Manage > Features"
        }), " and reading the following:"]
      }), "\n", _jsxs(_components.blockquote, {
        children: ["\n", _jsxs(_components.p, {
          children: [_jsx(_components.em, {
            children: "\"To analyze accounts in Heap, you need to specify a user property that can be used to group users into accounts (for example, Account ID)."
          }), "\""]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-heap-account-id-config.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing events, Hightouch treats any records added to ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "your source"
        }), " as new events and sends them to Heap when your sync runs."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "event-sync-modes",
        children: "Event sync modes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": ", _jsx(_components.em, {
            children: "only"
          }), " pushes new events to Heap."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsx(_components.p, {
        children: "To specify the name of the event you wish to sync to Heap you have the option of either\ninputting the name of a particular event or using a column from your model."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-heap-event-name.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "timestamp",
        children: "Timestamp"
      }), "\n", _jsx(_components.p, {
        children: "You may also specify the timestamp of the event in your source. However, be aware that this value\nmay be overridden by its respective mapping and is completely optional. If you choose not to explicitly specify\na timestamp then Heap will use the time the event arrives to the server."
      }), "\n", _jsx(_components.h3, {
        id: "identity",
        children: "Identity"
      }), "\n", _jsx(_components.p, {
        children: "The identity of an event is a required field and is typically a unique user property. If the specified identity\ndoesn't exist, a new user will be created with that identity in Heap."
      }), "\n", _jsx(_components.h2, {
        id: "field-mapping-events-and-objects",
        children: "Field mapping (events and objects)"
      }), "\n", _jsxs(_components.p, {
        children: ["You may also sync columns from your source to Heap as event, user, or account ", _jsx(_components.code, {
          children: "properties"
        }), ".\nAny property previously created will be updated and any new properties will be created."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-heap-field-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
